// =============================================================================
// Results — runs on results.html. Reads the stored inputs, calls classify(),
// and binds the Classification to `[data-ff-bind]` DOM slots.
//
// Covered by this module so far:
//   - Top-of-page block (topbar meta, eyebrow, title, deck)
//   - Severity flag on body (data-ff-severity) for CSS styling
//   - Urgent banner above the medium disclaimer
//   - Right sidebar "Your inputs" summary card
//   - Right sidebar "Generated" card (date + engine version)
//
// Detail sections (abstract <dl>, overview paragraph, gradient bars, lab
// table, symptoms, confidence paragraph, next steps) remain the static
// iron_running_low sample and will be wired up in follow-up commits.
// =============================================================================

import { classify } from '../engine/index.js';
import { routeNewsletter } from '../engine/newsletter-routing.js';
import type { RoutingPayload } from '../engine/newsletter-routing.js';
import { SPEC_METADATA } from '../engine/spec.js';
import type { Classification, LabInputs } from '../engine/types.js';
import { isClassifiable, load } from './form-state.js';
import { renderGradientBars } from './gradient-bars.js';
import { renderLabTable } from './lab-table.js';
import { renderPatternNarrative } from './pattern-renderer.js';

/** Friendly copy shown when the user lands on results.html without inputs. */
const NO_INPUTS_MESSAGE =
  'No lab values on file for this session. Start from step 1 to generate a result.';

// The hard gate has a single unlock condition: the user has subscribed.
// scripts.js sets this flag on a successful subscribe; pdf-preview.ts reads it
// too. There is no skip path, so "unlocked" and "subscribed" are the same thing.
const SUBSCRIBED_KEY = 'ff-newsletter-subscribed';

function readFlag(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

// -----------------------------------------------------------------------------
// Tiny DOM helpers
// -----------------------------------------------------------------------------

function setText(key: string, value: string): void {
  document.querySelectorAll<HTMLElement>(`[data-ff-bind="${key}"]`).forEach((el) => {
    el.textContent = value;
  });
}

function setAttr(key: string, attr: string, value: string): void {
  document.querySelectorAll<HTMLElement>(`[data-ff-bind="${key}"]`).forEach((el) => {
    el.setAttribute(attr, value);
  });
}

function show(key: string): void {
  document.querySelectorAll<HTMLElement>(`[data-ff-show="${key}"]`).forEach((el) => {
    el.hidden = false;
  });
}

/** Hide every `[data-ff-hide="key"]` block — inverse of show(). */
function hide(key: string): void {
  document.querySelectorAll<HTMLElement>(`[data-ff-hide="${key}"]`).forEach((el) => {
    el.hidden = true;
  });
}

/** Capitalize the first letter of a token like 'routine' → 'Routine'. */
function titleCase(s: string): string {
  return s.length ? s[0]!.toUpperCase() + s.slice(1) : s;
}

// -----------------------------------------------------------------------------
// Label maps — internal enums → human-facing copy
// -----------------------------------------------------------------------------

function labelLifeStage(stage: LabInputs['lifeStage']): string {
  switch (stage) {
    case 'non_pregnant_premenopausal':
      return 'Cycling';
    case 'pregnant_t1':
      return 'Pregnant (1st trimester)';
    case 'pregnant_t2':
      return 'Pregnant (2nd trimester)';
    case 'pregnant_t3':
      return 'Pregnant (3rd trimester)';
    case 'postpartum_within_6mo':
      return 'Postpartum (within 6 mo)';
    case 'perimenopausal':
      return 'Perimenopausal';
    case 'postmenopausal':
      return 'Postmenopausal';
    case 'none_of_these':
      return 'Not specified';
  }
}

function labelDiet(diet: LabInputs['diet']): string {
  return diet === 'plant_forward' ? 'Plant-forward' : 'Omnivore';
}

function labelRange(range: LabInputs['referenceRange']): string {
  return range === 'male' ? 'Male' : 'Female';
}

/** Plain-language GAHT label for the right-hand "Your inputs" card. */
function labelGaht(gaht: LabInputs['gaht']): string {
  switch (gaht) {
    case 'none':
      return 'None';
    case 'feminizing_12plus':
      return 'Estrogen-based · 12+ mo';
    case 'feminizing_under_12':
      return 'Estrogen-based · first year';
    case 'masculinizing_12plus':
      return 'Testosterone-based · 12+ mo';
    case 'masculinizing_under_12':
      return 'Testosterone-based · first year';
  }
}

/** True when the user is in the first 12 months of any GAHT regimen. */
function isFirstYearGaht(gaht: LabInputs['gaht']): boolean {
  return gaht === 'feminizing_under_12' || gaht === 'masculinizing_under_12';
}

/** True when the tool's corpus does not cover the user's physiology. */
function isOutOfScope(gaht: LabInputs['gaht']): boolean {
  return gaht === 'masculinizing_12plus';
}

function labelIronSupp(v: LabInputs['ironSupplementation'] | undefined): string {
  switch (v) {
    case 'currently':
      return 'Currently';
    case 'stopped_recent':
      return 'Stopped in last 4 weeks';
    case 'no':
    case undefined:
      return 'None';
  }
}

function labelSymptoms(syms: LabInputs['symptoms'] | undefined): string {
  if (!syms || syms.length === 0) return 'None reported';
  const map: Record<NonNullable<LabInputs['symptoms']>[number], string> = {
    unusual_fatigue: 'Fatigue',
    hair_shedding: 'Hair shedding',
    brittle_nails: 'Nails',
    restless_legs: 'Restless legs',
    pica: 'Pica',
    cold_intolerance: 'Cold intolerance',
  };
  return syms.map((s) => map[s]).join(', ');
}

function labelInflammation(inputs: LabInputs): string {
  if (inputs.recentIllness2Weeks === true) return 'Recent illness';
  if (inputs.crp !== undefined && inputs.crp >= 5) return `CRP ${inputs.crp} mg/L`;
  return 'None reported';
}

function formatGeneratedDate(d: Date = new Date()): string {
  // E.g., "22 Apr 2026"
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// -----------------------------------------------------------------------------
// Renderers — pure functions on the Classification / LabInputs
// -----------------------------------------------------------------------------

/** Human-facing severity label — Option A (action-focused) rename. */
function severityLabel(severity: Classification['severity']): string {
  switch (severity) {
    case 'urgent':
      return 'See a clinician today';
    case 'prompt':
      return 'Bring to your next appointment';
    case 'routine':
      return 'For your records';
  }
}

function renderSeverity(severity: Classification['severity']): void {
  document.body.dataset.ffSeverity = severity;
  setText('severity', severityLabel(severity));
  if (severity === 'urgent') show('ifSeverityUrgent');
}

function renderConfidence(confidenceTier: Classification['confidenceTier']): void {
  const label =
    confidenceTier === 'recalibration_flag'
      ? 'Recalibration flagged'
      : titleCase(confidenceTier);
  setText('confidence', label);
}

function renderTopbarMeta(laypersonName: string): void {
  // Show the plain-language pattern name (not the engine's internal id) so the
  // topbar reads as a sentence instead of code. The id itself stays available
  // on body[data-ff-pattern-id] for tooling / analytics.
  setText('patternMeta', `Your result · ${laypersonName}`);
}

function renderInputsCard(inputs: LabInputs): void {
  setText('inAge', String(inputs.age));
  // inRange retained for legacy markup; harmless no-op when the row is absent.
  setText('inRange', labelRange(inputs.referenceRange));
  setText('inLifeStage', labelLifeStage(inputs.lifeStage));
  setText('inGaht', labelGaht(inputs.gaht));
  setText('inDiet', labelDiet(inputs.diet));
  setText('inInflammation', labelInflammation(inputs));
  setText('inSupplements', labelIronSupp(inputs.ironSupplementation));
  setText('inSymptoms', labelSymptoms(inputs.symptoms));
}

function renderGeneratedCard(): void {
  setText(
    'generated',
    `${formatGeneratedDate()} · v${SPEC_METADATA.version}`,
  );
}

function renderNoInputs(): void {
  document.body.dataset.ffState = 'no-inputs';
  setText('patternMeta', 'Your result · no lab values entered');
  setText('eyebrow', 'No inputs');
  setText('patternLayperson', NO_INPUTS_MESSAGE);
  setText('patternTechnical', '');
  renderGeneratedCard();
  setAttr('patternMeta', 'data-ff-state', 'no-inputs');
  // Hide all the result-detail sections so we don't show the page's static
  // Stage 3 placeholder content (which would contradict the no-inputs
  // headline). The body's data-ff-state="no-inputs" is hooked in CSS so
  // the entire .results-layout is hidden and only the page-title-block +
  // a CTA back to step 1 remain visible.
}

/**
 * Render the out-of-scope screen shown to users whose physiology this tool's
 * corpus does not cover (currently: testosterone-based GAHT, 12+ months).
 * Hides the normal results markup and reveals the replacement block.
 */
function renderOutOfScope(inputs: LabInputs): void {
  document.body.dataset.ffState = 'out-of-scope';
  setText('patternMeta', 'Your result · out of scope');
  setText('eyebrow', 'Out of scope');
  setText('patternLayperson', 'This tool doesn\u2019t apply to your situation.');
  setText(
    'patternTechnical',
    'On long-term testosterone, red-cell values typically sit between cis-male and cis-female reference means. Neither range cleanly applies, and this tool\u2019s corpus is built on women\u2019s iron physiology.',
  );
  // Reveal the out-of-scope block; the [data-ff-hide="ifOutOfScope"] wrapper
  // around the rest of the center column hides the normal flow.
  show('ifOutOfScope');
  hide('ifOutOfScope');
  renderInputsCard(inputs);
  renderGeneratedCard();
}

function render(result: Classification, inputs: LabInputs): void {
  // Expose the engine pattern id on body for tooling / analytics without
  // rendering it as visible text.
  document.body.dataset.ffPatternId = result.patternId;

  renderTopbarMeta(result.laypersonName);
  setText('eyebrow', 'Your research-based read');
  setText('patternLayperson', result.laypersonName);
  setText('patternTechnical', result.technicalName);
  renderSeverity(result.severity);
  renderConfidence(result.confidenceTier);
  if (result.mostInformativeNextTest) {
    setText(
      'nextTestHint',
      `Most informative next test: ${result.mostInformativeNextTest}.`,
    );
  }
  renderInputsCard(inputs);
  renderGeneratedCard();
  renderLabTable(inputs, labelLifeStage(inputs.lifeStage));
  renderGradientBars(inputs);
  renderPatternNarrative(result, inputs);

  // First-year GAHT: surface a caveat banner above the abstract. The read
  // still runs, but confidence interpretation should factor in that the
  // corpus is thinner for first-year GAHT iron physiology.
  if (isFirstYearGaht(inputs.gaht)) show('ifGahtTransition');

  // Newsletter-routing: compute matched issues + Beehiiv custom_fields,
  // expose them to the subscribe forms (exit-intent + footer) on this page.
  // Beehiiv-side automations branch on these fields for pattern-specific
  // nurture sequences (PRD-01 §2.6 #8).
  const routing = routeNewsletter(result, inputs);
  populateRoutingContext(routing);
  renderKeepLearning(routing);
}

/**
 * Stamp the routing payload onto every newsletter subscribe form on the page,
 * via a single hidden input each form carries (`[data-routing-context]`).
 * `scripts.js` reads that JSON blob and includes it in the subscribe POST.
 *
 * Also exposed on `window.__FF_ROUTING__` for diagnostics and for any future
 * surface (in-page "Keep learning" off-ramp) that wants the matched-issues
 * list without re-running the engine.
 */
function populateRoutingContext(routing: RoutingPayload): void {
  // Window-level handle: read-only consumer convention.
  (window as unknown as { __FF_ROUTING__?: RoutingPayload }).__FF_ROUTING__ = routing;

  // Per-form hidden input. Forms that want routing context render
  // `<input type="hidden" data-routing-context value="" />` and we fill it.
  const json = JSON.stringify({
    customFields: routing.beehiivCustomFields,
    routingTags: routing.routingTags,
    severityOverride: routing.severityOverride,
  });
  document
    .querySelectorAll<HTMLInputElement>('[data-routing-context]')
    .forEach((el) => {
      el.value = json;
    });
}

/**
 * Render the "Keep learning" off-ramp (PRD-01 §2.6 #8a). Lists up to three
 * FF newsletter issues matched to the user's classification by the routing
 * engine, with the top match labeled "Read this next" and the others as
 * situational picks.
 *
 * Hides the entire section when there are no matches — rare, since the
 * `default` rule always fires, but defensive against future rule edits.
 */
function renderKeepLearning(routing: RoutingPayload): void {
  const list = document.querySelector<HTMLOListElement>(
    '[data-ff-bind="keepLearning"]',
  );
  if (!list) return;

  if (routing.matchedIssues.length === 0) return; // section stays hidden

  list.innerHTML = '';
  routing.matchedIssues.forEach((issue, idx) => {
    const li = document.createElement('li');
    li.className = idx === 0 ? 'keep-learning-item is-primary' : 'keep-learning-item';

    const label = document.createElement('p');
    label.className = 'keep-learning-label';
    label.textContent = idx === 0 ? 'Read this next' : 'Also worth a look';
    li.appendChild(label);

    const a = document.createElement('a');
    a.className = 'keep-learning-link';
    a.href = issue.url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = issue.title;
    li.appendChild(a);

    list.appendChild(li);
  });

  show('ifKeepLearning');
}

// -----------------------------------------------------------------------------
// Soft gate — teaser + email capture (PRD-01)
// -----------------------------------------------------------------------------

/**
 * Apply the lock state. `locked` shows the blocking modal (and CSS blurs the
 * page behind + locks scroll); `unlocked` hides it and reveals the read. We
 * also toggle `inert` on the gated wrapper so the blurred read is out of the
 * tab order / a11y tree while the modal is up, and move focus into the modal's
 * email field when locked.
 */
function setGate(unlocked: boolean): void {
  document.body.dataset.ffGate = unlocked ? 'unlocked' : 'locked';
  document.querySelectorAll<HTMLElement>('[data-ff-gated]').forEach((el) => {
    if (unlocked) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  });
  if (!unlocked) {
    const input = document.getElementById('gate-email') as HTMLInputElement | null;
    if (input) window.setTimeout(() => input.focus(), 50);
  }
}

/** Reveal the read only for a subscriber; everyone else hits the modal. */
function applyGateState(): void {
  setGate(readFlag(SUBSCRIBED_KEY));
}

function init(): void {
  const stored = load();
  if (!isClassifiable(stored)) {
    renderNoInputs();
    // Nothing to gate on the no-inputs screen — leave it open (the
    // results-layout is hidden by CSS anyway).
    setGate(true);
    return;
  }
  // Out-of-scope gate — render the routing screen instead of classifying.
  // The engine's masculinizing_12plus thresholds are not corpus-validated,
  // so we do not want to return a pattern read for this cohort.
  if (isOutOfScope(stored.gaht)) {
    renderOutOfScope(stored);
    // Out-of-scope shows its own screen, not a gated read — leave it open.
    setGate(true);
    return;
  }
  const result = classify(stored);
  render(result, stored);
  // Lock behind the modal, or reveal it for a returning subscriber.
  applyGateState();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
